import React, { createContext, useContext, useRef, useState } from 'react';
import ReCAPTCHA from 'react-google-recaptcha';

interface ContextProps {
  isVisible: boolean;
  errorVisible: boolean;
  recaptchaValue: string;
  enableRecaptcha: boolean;
  refRecaptcha: React.MutableRefObject<any>;
  showRecaptcha: () => void;
  resetRecaptcha: () => void;
  recaptchaExpire: () => void;
  showValidationError: () => void;
  resetErrors: () => void;
  setEnableRecaptcha: (value: boolean) => void;
  recaptchaChange: (value: string | null) => void;
  onCaptchaValidate: () => void;
  setOnCaptchaValidate: (callback: () => void) => void;
  setResetRecaptchaCallback: (callback: () => void) => void;
}

const RecaptchaContext = createContext<ContextProps>({} as ContextProps);

export const useEmailRecaptcha = () => useContext(RecaptchaContext);

export const EmailRecaptchaProvider: React.FC = ({ children }) => {
  const refRecaptcha = useRef<React.MutableRefObject<ReCAPTCHA>>(null);
  const [recaptchaShouldEnable, setRecaptchaShouldEnable] = useState<boolean>(false);
  const [errorVisible, setErrorVisible] = useState<boolean>(false);
  const [recaptchaValue, setRecaptchaValue] = useState<string>('');
  const [isVisible, setIsVisible] = useState<boolean>(false);
  const [onCaptchaValidate, setOnCaptchaValidate] = useState<() => void>(() => () => null);
  const [resetRecaptchaCallback, setResetRecaptchaCallback] = useState<() => void>(() => () => null);

  const resetErrors = () => {
    setErrorVisible(false);
  };

  const recaptchaExpire = () => {
    setRecaptchaValue('');
  };

  const recaptchaChange = (value: string | null) => {
    resetErrors();
    setRecaptchaValue(value || '');
    onCaptchaValidate();
  };

  const showRecaptcha = () => {
    setIsVisible(true);
  };

  const resetRecaptcha = () => {
    setRecaptchaValue('');
    resetRecaptchaCallback();
  };

  const setEnableRecaptcha = (value: boolean) => {
    setRecaptchaShouldEnable(value);
  };

  const showValidationError = () => {
    setErrorVisible(true);
  };

  const contextValues: ContextProps = {
    recaptchaValue,
    recaptchaExpire,
    recaptchaChange,
    isVisible,
    showRecaptcha,
    resetRecaptcha,
    refRecaptcha,
    enableRecaptcha: recaptchaShouldEnable,
    setEnableRecaptcha,
    errorVisible,
    onCaptchaValidate,
    showValidationError,
    resetErrors,
    setOnCaptchaValidate,
    setResetRecaptchaCallback,
  };

  return <RecaptchaContext.Provider value={contextValues}>{children}</RecaptchaContext.Provider>;
};
